import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings as SettingsIcon,
  Send,
  Link as LinkIcon,
  CheckCircle,
  Loader2,
  Bell,
} from 'lucide-react';
import {
  getTelegramStatusService,
  linkTelegramService,
  unlinkTelegramService,
  getBotInfoService,
} from '../../service/telegram';
import {
  getNotificationPreferencesService,
  updateNotificationPreferencesService,
} from '../../service/user';
import type { NotificationPreferences } from '../../service/user';
import { showToast } from '../../context/ToastContext';

// ─── Telegram state ──────────────────────────────────────────────────

type PageState = 'loading' | 'unlinked' | 'linked' | 'error';

const STEPS = [
  {
    title: 'Mở Telegram',
    description: 'Mở ứng dụng Telegram trên điện thoại hoặc máy tính',
  },
  {
    title: 'Chat với bot',
    description: (
      <>
        Tìm và chat với{' '}
        <a
          href="https://t.me/hu69_bot"
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-[#3b82f6] no-underline hover:underline"
        >
          @hu69_bot
        </a>
      </>
    ),
  },
  {
    title: 'Gõ lệnh /connect',
    description: (
      <>
        Gõ{' '}
        <code className="rounded border border-[#2d3448] bg-[#0d0f14] px-1.5 py-0.5 font-mono text-[11px] text-[#7dd3fc]">
          /connect
        </code>{' '}
        để bot gửi mã xác thực 6 ký tự
      </>
    ),
  },
  {
    title: 'Nhập mã xác thực',
    description: 'Nhập 6 ký tự vào ô bên dưới và nhấn "Liên kết"',
  },
];

/**
 * Danh sách toggle mà màn này biết render — đây là NGUỒN SỰ THẬT, không phải
 * response của server.
 *
 * Trước đây UI lặp `Object.keys(notifPrefs)`, tức là render theo bất cứ key nào
 * server trả về. Document của user cũ vẫn còn `betLog` (preference đã gỡ, chỉ
 * được set false chứ không xoá hẳn), nên khi bỏ nhãn `betLog` thì
 * `NOTIF_LABELS[key].label` thành undefined và cả trang Cài đặt trắng màn.
 * Lặp theo NOTIF_LABELS thì key lạ của server bị bỏ qua, key đã gỡ cũng vô hại.
 */
const NOTIF_LABELS: Record<keyof NotificationPreferences, { label: string; desc: string }> = {
  jackpotAlert: { label: 'Báo nổ hũ', desc: 'Thông báo khi tài khoản của bạn thực sự nổ hũ' },
  jackpotMonitor: { label: 'Theo dõi giá hũ', desc: 'Gửi thông báo giá trị hũ định kỳ 10 phút/lần' },
  lowBalance: { label: 'Số dư thấp', desc: 'Cảnh báo khi số dư tài khoản xuống thấp' },
  autoStop: { label: 'Dừng nhóm', desc: 'Thông báo khi nhóm bị dừng do hết tiền' },
  sessionReport: {
    label: 'Báo cáo cuối phiên',
    desc: 'Mỗi phiên 1 tin: kết quả, số dư trước/sau và lãi lỗ của mọi nhóm đã đánh',
  },
  proxyAlert: {
    label: 'Cảnh báo proxy chết',
    desc: 'Báo ngay khi proxy của tài khoản ngừng hoạt động — kèm acc game và nhóm bị ảnh hưởng',
  },
};

const NOTIF_KEYS = Object.keys(NOTIF_LABELS) as Array<keyof NotificationPreferences>;

// ─── Settings Page ───────────────────────────────────────────────────

const Settings: React.FC = () => {
  // ── Telegram state ─────────────────────────────────────────────────
  const [pageState, setPageState] = useState<PageState>('loading');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [linkedAt, setLinkedAt] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const codeStr = code.join('');

  const fetchStatus = useCallback(async () => {
    try {
      setErrorMsg(null);
      const [status, botInfo] = await Promise.all([
        getTelegramStatusService(),
        getBotInfoService(),
      ]);
      setBotUsername(botInfo.username);
      if (status.linked) {
        setLinkedAt(status.linkedAt ?? null);
        setPageState('linked');
      } else {
        setPageState('unlinked');
      }
    } catch {
      setPageState('error');
      setErrorMsg('Không thể kết nối server');
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const handleCodeChange = (index: number, value: string) => {
    const char = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 1);
    if (!char && value) return;

    const newCode = [...code];
    newCode[index] = char || '';
    setCode(newCode);
    setErrorMsg(null);

    if (char && index < 5) {
      const nextInput = document.getElementById(`settings-code-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`settings-code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .slice(0, 6);
    const newCode = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i] || '';
    }
    setCode(newCode);

    const nextIdx = Math.min(pasted.length, 5);
    const nextInput = document.getElementById(`settings-code-${nextIdx}`);
    nextInput?.focus();
  };

  const handleLink = async () => {
    if (codeStr.length !== 6) {
      setErrorMsg('Vui lòng nhập đủ 6 ký tự mã xác thực');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const result = await linkTelegramService(codeStr);
      if (result.status === 'success') {
        setPageState('linked');
        setCode(['', '', '', '', '', '']);
        setLinkedAt(new Date().toISOString());
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Liên kết thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlink = async () => {
    setSubmitting(true);
    try {
      await unlinkTelegramService();
      setPageState('unlinked');
      setLinkedAt(null);
    } catch (err: any) {
      setErrorMsg(err.message || 'Hủy liên kết thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const resetCode = () => {
    setCode(['', '', '', '', '', '']);
    setErrorMsg(null);
    document.getElementById('settings-code-0')?.focus();
  };

  // ── Notification preferences state ──────────────────────────────────
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences>({
    jackpotAlert: true,
    jackpotMonitor: true,
    lowBalance: true,
    autoStop: true,
    sessionReport: true,
    proxyAlert: true,
  });
  const [loadingNotifPrefs, setLoadingNotifPrefs] = useState(true);
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const data = await getNotificationPreferencesService();
        // Merge chứ không thay thế: server cũ chưa có key mới thì vẫn giữ default
        setNotifPrefs((prev) => ({ ...prev, ...data }));
      } catch {
        // keep defaults
      } finally {
        setLoadingNotifPrefs(false);
      }
    };
    fetchPrefs();
  }, []);

  const handleToggleNotif = async (key: keyof NotificationPreferences) => {
    const newPrefs = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(newPrefs);
    setSavingNotifPrefs(true);
    try {
      const result = await updateNotificationPreferencesService({ [key]: newPrefs[key] });
      if (result) {
        setNotifPrefs((prev) => ({ ...prev, ...result }));
      }
    } catch {
      setNotifPrefs(notifPrefs); // revert on error
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#3b82f6] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6 text-[#e2e8f0]">
      {/* Page Header */}
      <div className="mb-6 flex items-center gap-3.5">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#2d4a8a] bg-[#1e2a4a]">
          <SettingsIcon size={20} className="text-[#3b82f6]" />
        </div>
        <div>
          <h1 className="m-0 text-[18px] font-bold text-[#e2e8f0]">Cài đặt</h1>
          <p className="m-0 mt-0.5 text-[13px] text-[#64748b]">
            Cấu hình chung cho hệ thống
          </p>
        </div>
      </div>

      {/* ── Telegram Section ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#252a38] bg-[#161920] p-6">
        <div className="mb-4 flex items-center gap-2">
          <Send size={18} className="text-[#3b82f6]" />
          <h2 className="text-[15px] font-bold text-[#e2e8f0]">Telegram</h2>
        </div>

        {/* Error */}
        {errorMsg && (
          <div className="mb-4 rounded-lg border border-[#7f1d1d] bg-[#1c0a0a] px-4 py-3 text-[13px] font-semibold text-[#ef4444]">
            {errorMsg}
          </div>
        )}

        {/* Linked state */}
        {pageState === 'linked' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg border border-[#14532d] bg-[#0d1f14] px-4 py-3">
              <CheckCircle size={18} className="text-[#22c55e]" />
              <div className="text-[13px] text-[#e2e8f0]">
                Đã liên kết với{' '}
                <strong className="text-[#7dd3fc]">@{botUsername}</strong>
                {linkedAt && (
                  <span className="text-[11px] text-[#64748b]">
                    {' '}({new Date(linkedAt).toLocaleString('vi-VN')})
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleUnlink}
              disabled={submitting}
              className="rounded-lg border border-[#7f1d1d] bg-[#1c0a0a] px-4 py-2 text-[12px] font-bold text-[#ef4444] transition-colors hover:bg-[#2d0a0a] disabled:opacity-50"
            >
              {submitting ? 'Đang hủy...' : 'Ngắt kết nối'}
            </button>
          </div>
        )}

        {/* Unlinked state */}
        {pageState === 'unlinked' && (
          <div className="space-y-5">
            <ol className="space-y-3">
              {STEPS.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1e2a4a] text-[11px] font-bold text-[#3b82f6]">
                    {i + 1}
                  </span>
                  <div>
                    <span className="block text-[13px] font-bold text-[#e2e8f0]">{step.title}</span>
                    <span className="block mt-0.5 text-[12px] text-[#64748b]">{step.description}</span>
                  </div>
                </li>
              ))}
            </ol>

            <div className="rounded-lg border border-[#2d3448] bg-[#0d0f14] px-3 py-2.5 text-[12px] text-[#94a3b8]">
              💡 Muốn cả team cùng xem chung 1 nơi? Add bot vào 1 Telegram Group rồi gõ{' '}
              <code className="rounded border border-[#2d3448] bg-[#161920] px-1 py-0.5 font-mono text-[11px] text-[#7dd3fc]">
                /connect
              </code>{' '}
              ngay trong group đó (thay vì chat riêng với bot) — mỗi người trong nhóm lặp lại bước này bằng
              tài khoản của mình.
            </div>

            {/* OTP input */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex gap-1.5">
                {code.map((digit, i) => (
                  <input
                    key={i}
                    id={`settings-code-${i}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                    className="h-10 w-9 rounded-lg border border-[#252a38] bg-[#0d0f14] text-center text-[16px] font-bold text-[#e2e8f0] outline-none transition-colors focus:border-[#3b82f6]"
                    autoComplete="off"
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleLink}
                disabled={codeStr.length !== 6 || submitting}
                className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-4 py-2.5 text-[12px] font-bold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                <LinkIcon size={14} />
                {submitting ? 'Đang liên kết...' : 'Liên kết'}
              </button>
              {code.some(Boolean) && (
                <button
                  type="button"
                  onClick={resetCode}
                  className="text-[12px] font-semibold text-[#64748b] hover:text-[#e2e8f0]"
                >
                  Xoá
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error state */}
        {pageState === 'error' && (
          <div className="space-y-4">
            <p className="text-[13px] text-[#64748b]">
              Không thể kết nối đến server Telegram. Vui lòng thử lại sau.
            </p>
            <button
              type="button"
              onClick={() => { setPageState('loading'); void fetchStatus(); }}
              className="rounded-lg bg-[#3b82f6] px-4 py-2 text-[12px] font-bold text-white transition-colors hover:bg-[#2563eb]"
            >
              Thử lại
            </button>
          </div>
        )}
      </div>

      {/* ── Thông báo Section ────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#252a38] bg-[#161920] p-6">
        <div className="mb-4 flex items-center gap-2">
          <Bell size={18} className="text-[#3b82f6]" />
          <h2 className="text-[15px] font-bold text-[#e2e8f0]">Thông báo</h2>
          {savingNotifPrefs && <Loader2 size={14} className="animate-spin text-[#3b82f6]" />}
        </div>

        {loadingNotifPrefs ? (
          <div className="flex items-center gap-2 py-4 text-[12px] text-[#64748b]">
            <Loader2 size={14} className="animate-spin" />
            Đang tải cấu hình...
          </div>
        ) : (
          <div className="space-y-3">
            {NOTIF_KEYS.map((key) => (
              <div
                key={key}
                className="flex items-center justify-between rounded-lg border border-[#252a38] bg-[#0d0f14] px-4 py-3"
              >
                <div>
                  <span className="block text-[13px] font-bold text-[#e2e8f0]">
                    {NOTIF_LABELS[key].label}
                  </span>
                  <span className="block mt-0.5 text-[11px] text-[#64748b]">
                    {NOTIF_LABELS[key].desc}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleNotif(key)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    notifPrefs[key] ? 'bg-[#22c55e]' : 'bg-[#334155]'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                      notifPrefs[key] ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export { Settings };
